import { DeviceType } from '@/constants/device';
import { downloadTrackingService } from './downloadTrackingService';
import { wallpaperService } from '@/firebase/services/wallpaperService';
import { generateDownloadFilename, isValidDownloadUrl } from '@/utils/downloadUtils';
import { centralizedImageSecurity } from './centralizedImageSecurity';
//import { auth } from '@/firebase/config';

export interface DownloadOptions {
  wallpaperId: string;
  imageUrl: string;
  filename?: string;
  resolution: string;
  deviceType?: DeviceType;
  isOptimal?: boolean;
}

export interface DownloadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface DownloadResult {
  success: boolean;
  filename: string;
  error?: string;
}

class DownloadService {
  private activeDownloads = new Map<string, AbortController>();

  /**
   * Downloads a wallpaper with secure authentication and tracking
   */
  async downloadWallpaper(options: DownloadOptions): Promise<DownloadResult> {
    const {
      wallpaperId,
      filename,
      resolution,
      deviceType,
      isOptimal = false
    } = options;

    // Generate consistent filename
    const downloadFilename = filename || generateDownloadFilename(wallpaperId, resolution, deviceType, undefined, options.imageUrl);
    
    // Clear any previous download state for this wallpaper
    if (this.activeDownloads.has(wallpaperId)) {
      this.activeDownloads.delete(wallpaperId);
    }

    try {
      // Check if download is already in progress for this wallpaper
      if (this.activeDownloads.has(wallpaperId)) {
        const errorMsg = 'A download for this wallpaper is already in progress';
        console.warn(`[DownloadService] ${errorMsg}`);
        throw new Error(errorMsg);
      }

      // Create abort controller for this download
      const abortController = new AbortController();
      this.activeDownloads.set(wallpaperId, abortController);

      // Get secure download URL with metadata and watermarking
      // This call includes the download permission check for anonymous users
      console.log(`[DownloadService] Requesting secure download with metadata for wallpaper: ${wallpaperId}`);
      
      // Get secure download URL with metadata and watermarking - this will throw if security check fails
      const secureResult = await centralizedImageSecurity.getSecureDownloadWithMetadata({
        imageUrl: options.imageUrl,
        wallpaperId,
        isPreview: false // This is a download
      });
      console.log(`[DownloadService] Secure download with metadata obtained successfully:`, {
        downloadId: secureResult.metadata?.downloadId,
        fingerprint: secureResult.fingerprint,
        hasWatermark: true
      });

      // Track download start
      if (deviceType) {
        downloadTrackingService.trackDeviceDownload({
          wallpaperId,
          deviceType,
          resolution,
          isOptimal
        });
      }

      // Perform the secure download
      await this.performSecureDownload(
        secureResult.secureUrl,
        downloadFilename,
        abortController.signal,
        wallpaperId
      );

      // Only increment download count if the download was successful
      try {
        await wallpaperService.incrementDownloadCount(wallpaperId);
        console.log(`[DownloadService] Successfully downloaded: ${downloadFilename}`);
      } catch (dbError) {
        console.error('[DownloadService] Failed to update download count:', dbError);
        // Don't fail the download if we can't update the count
      }

      return {
        success: true,
        filename: downloadFilename
      };
    } catch (error) {
      console.error('[DownloadService] Download failed:', error);
      
      // Check if this is a security/permission error that should be shown to the user
      if (error instanceof Error && error.message.includes('Download limit exceeded')) {
        // Re-throw security errors so the UI can show the proper message
        throw error;
      }
      
      // For other errors, return a failed result
      return {
        success: false,
        filename: downloadFilename,
        error: error instanceof Error ? error.message : 'Download failed'
      };
    } finally {
      // Clean up active download tracking
      this.activeDownloads.delete(wallpaperId);
    }
  }

  /**
   * Cancels an active download
   */
  cancelDownload(wallpaperId: string): void {
    const controller = this.activeDownloads.get(wallpaperId);
    if (controller) {
      controller.abort();
      this.activeDownloads.delete(wallpaperId);
    }
  }

  /**
   * Checks if a download is currently active
   */
  isDownloadActive(wallpaperId: string): boolean {
    return this.activeDownloads.has(wallpaperId);
  }

  /**
   * Gets all active downloads
   */
  getActiveDownloads(): string[] {
    return Array.from(this.activeDownloads.keys());
  }

  /**
   * Performs secure download with enhanced security measures
   * The secure URL is now a blob URL that prevents direct access
   */
  private async performSecureDownload(
    secureUrl: string,
    filename: string,
    signal: AbortSignal,
    wallpaperId: string
  ): Promise<void> {
    try {
      // Check if this is already a blob URL (from our security service)
      const isBlobUrl = secureUrl.startsWith('blob:');
      
      if (isBlobUrl) {
        // Direct download from secure blob URL
        await this.downloadFromBlobUrl(secureUrl, filename, wallpaperId);
      } else {
        // Legacy fallback for direct URLs (should be rare now)
        await this.downloadFromDirectUrl(secureUrl, filename, signal, wallpaperId);
      }
    } catch (error) {
      console.error('[DownloadService] Secure download failed:', error);
      throw error;
    }
  }

  /**
   * Downloads from a secure blob URL
   */
  private async downloadFromBlobUrl(blobUrl: string, filename: string, wallpaperId: string): Promise<void> {
    try {
      // Fetch the blob data
      const response = await fetch(blobUrl);
      if (!response.ok) {
        throw new Error('Failed to access secure download URL');
      }

      const blob = await response.blob();

      // Verify it's an image or video
      if (!blob.type.startsWith('image/') && !blob.type.startsWith('video/')) {
        throw new Error('Invalid file type received');
      }

      // Additional security: verify blob size is reasonable
      if (blob.size > 100 * 1024 * 1024) { // 100MB limit
        throw new Error('File size exceeds security limits');
      }

      // Create temporary download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';

      // Add security attributes
      link.setAttribute('data-wallpaper-id', wallpaperId);
      link.setAttribute('data-download-time', Date.now().toString());
      link.setAttribute('data-secure-download', 'true');

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Immediate cleanup
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);

    } catch (error) {
      console.error('[DownloadService] Blob download failed:', error);
      throw new Error('Secure download failed');
    }
  }

  /**
   * Downloads from a direct URL (legacy fallback)
   */
  private async downloadFromDirectUrl(
    imageUrl: string,
    filename: string,
    signal: AbortSignal,
    wallpaperId: string
  ): Promise<void> {
    // For Cloudinary URLs, we need to handle them differently
    const isCloudinaryUrl = imageUrl.includes('res.cloudinary.com');
    
    // Add security headers for the download request
    const headers = new Headers({
      'X-Wallpaper-ID': wallpaperId,
      'X-Download-Type': 'secure',
      'X-Timestamp': Date.now().toString()
    });

    // Fetch the image
    const response = await fetch(imageUrl, {
      signal,
      // Only include credentials for non-Cloudinary URLs
      credentials: isCloudinaryUrl ? 'omit' : 'include',
      // Only include custom headers for non-Cloudinary URLs
      headers: isCloudinaryUrl ? undefined : headers
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch secure image: ${response.status} ${response.statusText}`);
    }

    // Verify content type
    const contentType = response.headers.get('content-type');
    if (!contentType?.startsWith('image/') && !contentType?.startsWith('video/')) {
      throw new Error('Invalid file type received');
    }

    // Get the blob
    const blob = await response.blob();

    // Additional security: verify blob size is reasonable
    if (blob.size > 100 * 1024 * 1024) { // 100MB limit
      throw new Error('File size exceeds security limits');
    }

    // Create secure download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    // Add security attributes
    link.setAttribute('data-wallpaper-id', wallpaperId);
    link.setAttribute('data-download-time', Date.now().toString());

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup with delay to ensure download starts
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    }, 1000);
  }

  /**
   * Legacy method for backward compatibility
   */
  private async performDownload(
    imageUrl: string,
    filename: string,
    signal: AbortSignal
  ): Promise<void> {
    // Validate URL first
    if (!isValidDownloadUrl(imageUrl)) {
      throw new Error('Invalid download URL');
    }

    // Fetch the image
    const response = await fetch(imageUrl, { signal });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    // Get the blob
    const blob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }



  /**
   * Validates download URL before attempting download
   */
  private async validateDownloadUrl(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Gets download statistics
   */
  getDownloadStats() {
    return {
      activeDownloads: this.activeDownloads.size,
      totalTrackedDownloads: downloadTrackingService.getOverallAnalytics().totalDownloads
    };
  }
}

export const downloadService = new DownloadService();